import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Spinner } from 'react-bootstrap'
import moment from 'moment'
import { FaRegTrashAlt } from 'react-icons/fa'
import { MdRefresh, MdChevronLeft, MdChevronRight, MdFileDownload } from 'react-icons/md'
import {
  getYears,
  getWeeks,
  getTerms,
  getLessons,
  getStatement,
  resetTeacherSchedule,
} from '../../actions/teacherSchedule'
import { createNotification } from '../notifications/notifications'
import usePageWidth from '../shared/usePageWidth'
import CustomSelect from '../CustomSelect'
import { buildWeek } from './buildWeek'
import { createStatement, STATEMENT_FILE_NAME } from './statementPdf'
import ScheduleTable from './ScheduleTable'
import LessonModal from './LessonModal'
import styles from './styles.module.css'

const SHEET_FORMATS = ['A3', 'A4']

const asDate = date => (date ? moment(date, 'YYYY-MM-DD').format('DD.MM.YYYY') : '')

// Наименование недели — её номер и даты начала и окончания (п. 9.2 ТЗ):
// справочное наименование с сервера на разных неделях записано по-разному
const weekLabel = week => `Неделя ${week.number} · ${asDate(week.date_begin)} – ${asDate(week.date_end)}`

const TeacherSchedule = () => {
  usePageWidth('pageWide')

  const dispatch = useDispatch()

  const years = useSelector(state => state.teacherSchedule.years)
  const weeks = useSelector(state => state.teacherSchedule.weeks)
  const weeksYearId = useSelector(state => state.teacherSchedule.weeksYearId)
  const terms = useSelector(state => state.teacherSchedule.terms)
  const termsYearId = useSelector(state => state.teacherSchedule.termsYearId)
  const lessons = useSelector(state => state.teacherSchedule.lessons)
  const totalLessons = useSelector(state => state.teacherSchedule.totalLessons)
  const totalHours = useSelector(state => state.teacherSchedule.totalHours)
  const lessonsWeekId = useSelector(state => state.teacherSchedule.lessonsWeekId)
  const yearsLoaded = useSelector(state => state.teacherSchedule.yearsLoaded)
  const isLoading = useSelector(state => state.teacherSchedule.isLoading)
  const { lastname, firstname, patronymic } = useSelector(state => state.auth)

  const [yearId, setYearId] = useState(null)
  const [weekId, setWeekId] = useState(null)
  const [termId, setTermId] = useState(null)
  const [sheetFormat, setSheetFormat] = useState(SHEET_FORMATS[0])
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pair, setPair] = useState(null)

  useEffect(() => {
    dispatch(getYears())
    return () => dispatch(resetTeacherSchedule())
  }, [dispatch])

  // текущий учебный год — по признаку с сервера, клиент его не вычисляет (п. 2.3 ТЗ)
  useEffect(() => {
    if (yearId !== null || !years.length) return
    setYearId((years.find(year => year.is_current) || years[0]).id)
  }, [years, yearId])

  // перечни недель и учебных периодов зависят от учебного года (пп. 2.5, 7.4 ТЗ)
  useEffect(() => {
    if (yearId === null) return
    dispatch(getWeeks(yearId))
    dispatch(getTerms(yearId))
  }, [dispatch, yearId])

  /* Текущая неделя — по признаку с сервера; если признак не пришёл ни у одной
     недели, открывается первая неделя года. Перечень недель приходит в порядке
     от первой к последней и на месте не переворачивается. */
  useEffect(() => {
    if (weekId !== null || weeksYearId !== yearId || !weeks.length) return
    setWeekId((weeks.find(week => week.is_current) || weeks[0]).id)
  }, [weeks, weeksYearId, yearId, weekId])

  // учебный период по умолчанию — текущий, а если такого нет — первый (п. 7.4 ТЗ)
  useEffect(() => {
    if (termsYearId !== yearId) return
    setTermId(terms.length ? (terms.find(term => term.is_current) || terms[0]).id : null)
  }, [terms, termsYearId, yearId])

  useEffect(() => {
    if (weekId === null) return
    dispatch(getLessons(weekId))
  }, [dispatch, weekId])

  const days = useMemo(() => buildWeek(lessons), [lessons])
  const today = moment().format('YYYY-MM-DD')

  const yearOptions = useMemo(
    () => years.map(year => ({ name: year.name, value: year.id })),
    [years]
  )
  const weekOptions = useMemo(
    () => weeks.map(week => ({ name: weekLabel(week), value: week.id })),
    [weeks]
  )
  const termOptions = useMemo(
    () => terms.map(term => ({ name: term.name, value: term.id })),
    [terms]
  )

  const weekIndex = weeks.findIndex(week => week.id === weekId)

  // отбор по умолчанию, к которому возвращает «Сбросить фильтры» (п. 2.8 ТЗ)
  const defaultYearId = years.length ? (years.find(year => year.is_current) || years[0]).id : null
  const defaultWeekId = weeksYearId === defaultYearId && weeks.length
    ? (weeks.find(week => week.is_current) || weeks[0]).id
    : null
  const isDirty = yearId !== null
    && (yearId !== defaultYearId || (defaultWeekId !== null && weekId !== defaultWeekId))

  const changeYear = (name, value) => {
    setYearId(value)
    // неделя подберётся заново, когда придёт перечень недель нового года
    setWeekId(null)
  }

  const stepWeek = step => {
    const next = weeks[weekIndex + step]
    if (next) setWeekId(next.id)
  }

  const resetFilters = () => {
    setYearId(defaultYearId)
    setWeekId(defaultYearId === yearId ? defaultWeekId : null)
  }

  const buildStatementDoc = async () => {
    if (termId === null || pdfLoading) return

    setPdfLoading(true)
    try {
      const data = await dispatch(getStatement(termId))
      if (!data) return
      if (!data.lessons || !data.lessons.length) {
        createNotification('warning', 'За выбранный учебный период занятий нет')
        return
      }
      createStatement(data, sheetFormat).save(STATEMENT_FILE_NAME)
    } finally {
      setPdfLoading(false)
    }
  }

  const teacherName = [lastname, firstname, patronymic].filter(Boolean).join(' ')

  /* Индикатор висит не только на время запроса: пока подбираются учебный год и
     неделя, а в состоянии лежит расписание другой недели, показывать таблицу
     нельзя — иначе на смене года мелькает чужая неделя, а пустое состояние
     появляется до того, как расписание вообще запрошено (п. 1.6 ТЗ). */
  const showLoader = isLoading
    || !yearsLoaded
    || (years.length > 0 && (yearId === null || weeksYearId !== yearId))
    || (weeks.length > 0 && (weekId === null || lessonsWeekId !== weekId))

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.headMain}>
          <h1 className={styles.title}>Личное расписание преподавателя</h1>
          <p className={styles.subtitle}>
            Преподаватель: <b>{teacherName || '—'}</b>
          </p>
        </div>
        <div className={styles.headRight}>
          <button
            type="button"
            className={styles.resetBtn}
            onClick={resetFilters}
            disabled={!isDirty}
          >
            Сбросить фильтры
            <FaRegTrashAlt size={16} />
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.fYear}>
          <CustomSelect
            title="Учебный год"
            width="100%"
            options={yearOptions}
            value={yearId}
            setFieldValue={changeYear}
            valuesName="yearId"
            disabled={!years.length}
            withoutAll
          />
        </div>

        <div className={styles.weekBox}>
          <button
            type="button"
            className={styles.stepBtn}
            onClick={() => stepWeek(-1)}
            disabled={weekIndex <= 0}
            title="Предыдущая неделя"
            aria-label="Предыдущая неделя"
          >
            <MdChevronLeft size={22} />
          </button>
          <div className={styles.fWeek}>
            <CustomSelect
              title="Неделя"
              width="100%"
              options={weekOptions}
              value={weekId}
              setFieldValue={(name, value) => setWeekId(value)}
              valuesName="weekId"
              disabled={!weekOptions.length}
              withoutAll
            />
          </div>
          <button
            type="button"
            className={styles.stepBtn}
            onClick={() => stepWeek(1)}
            disabled={weekIndex < 0 || weekIndex >= weeks.length - 1}
            title="Следующая неделя"
            aria-label="Следующая неделя"
          >
            <MdChevronRight size={22} />
          </button>
        </div>

        <button
          type="button"
          className={styles.refreshBtn}
          onClick={() => weekId !== null && dispatch(getLessons(weekId))}
        >
          <MdRefresh size={17} />
          Обновить
        </button>
      </div>

      <div className={styles.listbox}>
        <div className={styles.tableWrap}>
          {days.length ? (
            <ScheduleTable days={days} today={today} onOpenPair={setPair} />
          ) : (
            /* пустое состояние выводится внутри рамки таблицы, шапка при этом
               не выводится (п. 8.2 ТЗ) */
            <div className={styles.empty}>
              <div className={styles.emptyTitle}>На выбранной неделе занятий нет</div>
              Выберите другую неделю или другой учебный год
            </div>
          )}
        </div>

        {showLoader && (
          <div className={styles.load}>
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        )}
      </div>

      <div className={styles.footrow}>
        <span className={styles.total}>Занятий на неделе: <b>{totalLessons}</b></span>
        <span className={styles.total}>Всего часов: <b>{totalHours}</b></span>
      </div>

      {/* блок выписки выводится всегда, в том числе при неделе без занятий (п. 7.1 ТЗ) */}
      <div className={styles.extract}>
        <div className={styles.extractTitle}>Выписка из расписания</div>
        <div className={styles.extractHint}>
          Выписка формируется за учебный период по преподавателю, вошедшему в систему,
          и сохраняется файлом PDF.
        </div>
        <div className={styles.extractRow}>
          <div className={styles.fTerm}>
            <CustomSelect
              title="Учебный период"
              width="100%"
              options={termOptions}
              value={termId}
              setFieldValue={(name, value) => setTermId(value)}
              valuesName="termId"
              disabled={!termOptions.length}
              withoutAll
            />
          </div>

          <div className={styles.format}>
            <span className={styles.formatLabel}>Формат листа</span>
            <div className={styles.seg}>
              {SHEET_FORMATS.map(format => (
                <button
                  key={format}
                  type="button"
                  className={format === sheetFormat ? styles.segOn : undefined}
                  onClick={() => setSheetFormat(format)}
                >
                  {format}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className={styles.extractBtn}
            onClick={buildStatementDoc}
            disabled={termId === null || pdfLoading}
          >
            {pdfLoading
              ? <Spinner animation="border" size="sm" role="status" />
              : <MdFileDownload size={17} />}
            Сформировать выписку
          </button>
        </div>
      </div>

      <LessonModal pair={pair} onClose={() => setPair(null)} />
    </div>
  )
}

export default TeacherSchedule
